/**
 * Daily WebRTC session wrapper — the audio plane for voice mode.
 *
 * Replaces the OpenAI Realtime PCM-over-WS path (`../realtime-session.ts`,
 * deleted in P1.8). The widget no longer touches raw PCM frames at
 * all; Daily handles mic capture, encoding, and SFU forwarding.
 *
 * Surface:
 *   startVoiceSession(opts) → Promise<void>      one call to bring it up
 *   stopVoiceSession()       → Promise<void>     tear-down on close
 *   subscribeVoiceEvents()  → already wired via SSE in `data-plane.ts`
 *
 * State transitions land in `voiceState` (signal-backed). UI
 * components read from there.
 *
 * The bot lives in the same Daily room as a separate participant. We
 * detect it via `participant-joined` (audio-only, owner=true). When the
 * bot's audio track starts/stops, we infer SPEAKING / LISTENING. Daily
 * also gives us a `network-quality-change` event for DEGRADED.
 */

import type {
  DailyCall,
  DailyEventObjectAvailableDevicesUpdated,
  DailyEventObjectFatalError,
  DailyEventObjectNetworkQualityEvent,
  DailyEventObjectParticipant,
  DailyEventObjectParticipantLeft,
  DailyEventObjectTrack,
} from '@daily-co/daily-js';
import { CALL_OBJECT_OPTIONS, isDailyPreloaded, loadDaily, takePreloadedCall } from './daily-loader';
import { connectTimingPayload, type ConnectMarks } from './connect-timing';
import { postSessionEvent } from '../api';
import { visitSnapshot } from '../visit-log';
import { botTalking, botVideoStream, voiceState, voiceTransition } from './voice-state';
import { RESPAWN_JOIN_TIMEOUT_MS, shouldRespawnBot } from './bot-respawn';
import { createPlayoutMonitor, type PlayoutMonitor } from './playout-monitor';

export type VoiceTokenResponse = {
  sessionId: string;
  roomUrl: string;
  token: string;
  agentId: string;
  expiresAt: number;
  /** Daily room name — persisted so the next page can ask to rejoin it. */
  roomName?: string;
  /** Room's unix-seconds expiry — gates whether a rejoin is still worth trying. */
  roomExpiresAt?: number;
  /**
   * Server HMAC binding this room to this session. Persisted + handed back on
   * the next page so the server can trust the rejoin without storing state.
   * Absent when the server has no signing key configured (rejoin then just
   * cold-starts).
   */
  roomSig?: string;
  /**
   * True when the server reconnected us to an EXISTING room (warm rejoin)
   * rather than minting a fresh one + spawning a bot. The widget uses this
   * to run a short "is the bot actually here?" watchdog and cold-retry if
   * the bot's grace window had already elapsed.
   */
  reused?: boolean;
};

/** A room to attempt rejoining instead of cold-summoning a new one. */
export type RejoinRoom = {
  roomName: string;
  roomUrl: string;
  expiresAt: number;
  /** Server binding signature (see VoiceTokenResponse.roomSig). */
  sig: string;
};

export type StartVoiceSessionOptions = {
  /** Public-facing agent id (UUID). Used for /v1/voice/token + /v1/voice/events. */
  publicId: string;
  /** chat-api base URL — e.g. `https://api.showpilot.com`. */
  chatApiBaseUrl: string;
  /** Visitor's current page URL — passed to server for same-origin checks. */
  pageUrl?: string;
  /** Presenter — the hub this call presents; server threads it to the bot. */
  projectPublicId?: string | null;
  /** Single-tour door — threads with it so the block opens on that story. */
  tourPublicId?: string | null;
  /** The call started from the presenter door (data-showpilot-present). */
  presentDoor?: boolean;
  /**
   * Existing chat session id when the visitor has already exchanged
   * text-mode messages. Lets voice + text interleave under one
   * chat_sessions row instead of fragmenting into separate sessions.
   */
  sessionId?: string | null;
  /**
   * Called when the BOT ends the call on its own (idle timeout) — signalled
   * via a `showpilot:session-ended` Daily app-message. This is an
   * intentional end, NOT a crash/drop: the caller should tear down cleanly
   * and clear cross-page continuity so voice doesn't auto-reopen.
   */
  onBotEnded?: () => void;
  /**
   * When set, ask the server to rejoin THIS room (warm reconnect after a
   * full page reload) instead of minting a new room + bot. Passed straight
   * through to /v1/voice/token; the server validates + falls back to cold
   * if the room is gone.
   */
  rejoinRoom?: RejoinRoom | null;
  /**
   * With `rejoinRoom`: the bot vanished from the live room; ask the server
   * to spawn a fresh one into the SAME room (bot-respawn.ts). Internal —
   * set by the respawn path, never by a caller.
   */
  respawnBot?: boolean;
};

/**
 * The options and room of the session in progress, kept so a bot that
 * vanishes mid-call can be respawned into the same room with the same
 * stance (bot-respawn.ts). Cleared on stop.
 */
let activeOpts: StartVoiceSessionOptions | null = null;
let activeRoom: RejoinRoom | null = null;
let activeSessionId: string | null = null;
let respawnAttempts = 0;
let respawnJoinTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Set by wireCallEvents for the live call: runs the bot-gone path (the
 * same one Daily's `participant-left` takes) when chat-api says the bot
 * process died. Returns false when there is no bot to replace.
 */
let botDroppedHook: (() => boolean) | null = null;

/** Test/diagnostic seam: how many times this call respawned its bot. */
export function botRespawnCount(): number {
  return respawnAttempts;
}

/**
 * chat-api's `voice-bot-dropped` SSE event: the spawn server saw the bot
 * process die. Daily would only report the ghost ~30 s later (peer
 * timeout); run the respawn now. The later `participant-left` for the
 * dead bot is ignored because its id no longer matches. Returns whether
 * a respawn was started.
 */
export function notifyVoiceOfBotDropped(): boolean {
  if (!activeCall || !botDroppedHook) return false;
  return botDroppedHook();
}

/**
 * Holds the live Daily call object for the duration of one session.
 * Re-using across sessions is brittle (Daily's state survives `leave`
 * but you have to remember to unsubscribe everything); we create fresh
 * per startVoiceSession instead.
 */
let activeCall: DailyCall | null = null;

/**
 * Set when the bot signals an intentional end (idle timeout) via a
 * `showpilot:session-ended` app-message. Two effects: (1) the bot's
 * subsequent `participant-left` is treated as a clean end, not a crash,
 * so we skip the DEGRADED flash; (2) the registered `onBotEnded` callback
 * runs so the caller clears cross-page continuity (no auto-reopen).
 */
let botEndedIntentionally = false;
let onBotEndedCb: (() => void) | null = null;
/** Safety net: tear down even if the bot's participant-left never lands. */
let botEndFallbackTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Tear-down handlers we register during start; called by stop and on
 * error paths so we don't leak listeners or audio elements.
 */
let teardownFns: Array<() => void> = [];

/**
 * In headless mode (`createCallObject`), Daily does NOT auto-attach
 * remote audio tracks to playable elements — applications render
 * however they want. We do the minimum: one hidden <audio autoplay>
 * per remote audio track, removed on track-stopped. Without this,
 * the bot speaks but the visitor hears silence.
 *
 * Keyed by track id so cleanup is exact even when multiple bot
 * tracks come and go (TTS segments).
 */
const audioSinks = new Map<string, HTMLAudioElement>();

/** Unbound voice-sync law: the bot's audio is HELD (muted) from call
 * start until the stage's crossfade reveal, so her voice never lands
 * before her live face. Applies to current and future sinks. */
let botAudioMuted = false;
export function setBotAudioMuted(muted: boolean): void {
  botAudioMuted = muted;
  for (const audio of audioSinks.values()) audio.muted = muted;
}

/**
 * PLAYOUT ACK (presenter rails 1.1) — WebAudio ears on the bot's audio.
 *
 * The bot can only see TTS GENERATION edges; the widget is the only
 * party that hears PLAYOUT. An AnalyserNode taps every bot audio track
 * (analyser only — the hidden <audio> sink above still does the actual
 * playing), a 100ms RMS sampler feeds the pure state machine in
 * playout-monitor.ts, and when a bot turn has finished generating AND
 * the line has been silent for the window we send ONE
 * `showpilot:playout-done` app message. The bot's flow observer treats
 * it as pacing truth and demotes its chars/sec estimate to a watchdog.
 * Every failure path here degrades to "no acks" — which is exactly the
 * pre-slice behavior — and must never break audio itself.
 */
let playoutCtx: AudioContext | null = null;
let playoutAnalyser: AnalyserNode | null = null;
const playoutSources = new Map<string, MediaStreamAudioSourceNode>();
let playoutTimer: ReturnType<typeof setInterval> | null = null;
let playoutMonitor: PlayoutMonitor | null = null;
let playoutSampleBuf: Uint8Array<ArrayBuffer> | null = null;

/**
 * Session-plane logger set by index.tsx — mirrors each ack into the
 * session event log (`presenter-playout`) while a tour is on stage, so
 * pacing is replayable next to presents/controls/screen acks.
 */
let onPlayoutLoggedCb: (() => void) | null = null;
export function setPlayoutLogListener(cb: (() => void) | null): void {
  onPlayoutLoggedCb = cb;
}

function sendPlayoutDone(): void {
  if (!activeCall) return;
  try {
    activeCall.sendAppMessage({ type: 'showpilot:playout-done' }, '*');
  } catch (err) {
    console.warn('[voice/daily-session] playout-done app-message failed', err);
    return; // nothing landed — don't log a ghost ack
  }
  try {
    onPlayoutLoggedCb?.();
  } catch {
    /* logging must never break the call */
  }
}

function ensurePlayoutEars(): void {
  if (playoutTimer) return;
  try {
    playoutCtx = new AudioContext();
    playoutAnalyser = playoutCtx.createAnalyser();
    playoutAnalyser.fftSize = 512;
    playoutSampleBuf = new Uint8Array(playoutAnalyser.fftSize);
    playoutMonitor = createPlayoutMonitor({
      onPlayoutDone: sendPlayoutDone,
      // The one-voice law's talk signal follows what the room actually
      // plays, not what the bot finished generating (see the monitor).
      onAudible: (on) => {
        botTalking.value = on;
      },
    });
    playoutTimer = setInterval(() => {
      if (!playoutAnalyser || !playoutSampleBuf || !playoutMonitor) return;
      playoutAnalyser.getByteTimeDomainData(playoutSampleBuf);
      let sum = 0;
      for (let i = 0; i < playoutSampleBuf.length; i++) {
        const d = ((playoutSampleBuf[i] ?? 128) - 128) / 128;
        sum += d * d;
      }
      playoutMonitor.sample(Math.sqrt(sum / playoutSampleBuf.length), Date.now());
    }, 100);
    // The "Talk Now" click that started the call is our user gesture;
    // resume() is a no-op when the context is already running.
    void playoutCtx.resume().catch(() => undefined);
  } catch (err) {
    console.warn('[voice/daily-session] playout ears unavailable', err);
    stopPlayoutEars();
  }
}

function attachPlayoutSource(track: MediaStreamTrack): void {
  ensurePlayoutEars();
  if (!playoutCtx || !playoutAnalyser || playoutSources.has(track.id)) return;
  try {
    const src = playoutCtx.createMediaStreamSource(new MediaStream([track]));
    src.connect(playoutAnalyser);
    playoutSources.set(track.id, src);
  } catch (err) {
    console.warn('[voice/daily-session] playout source attach failed', err);
  }
}

function detachPlayoutSource(trackId: string): void {
  const src = playoutSources.get(trackId);
  if (!src) return;
  try {
    src.disconnect();
  } catch {
    /* already gone */
  }
  playoutSources.delete(trackId);
}

function stopPlayoutEars(): void {
  if (playoutTimer) {
    clearInterval(playoutTimer);
    playoutTimer = null;
  }
  for (const id of [...playoutSources.keys()]) detachPlayoutSource(id);
  if (playoutCtx) void playoutCtx.close().catch(() => undefined);
  playoutCtx = null;
  playoutAnalyser = null;
  playoutSampleBuf = null;
  playoutMonitor = null;
}

export async function startVoiceSession(
  opts: StartVoiceSessionOptions,
): Promise<VoiceTokenResponse> {
  if (activeCall) {
    // Idempotent — caller might double-click the mic button. Surface
    // the in-flight state rather than racing two sessions.
    throw new Error('voice session already active');
  }

  // Reset intentional-end bookkeeping for this fresh session.
  botEndedIntentionally = false;
  onBotEndedCb = opts.onBotEnded ?? null;
  if (botEndFallbackTimer) {
    clearTimeout(botEndFallbackTimer);
    botEndFallbackTimer = null;
  }

  // 1. Mint server-side: sessionId, Daily room, visitor token — and
  // fetch the lazy Daily SDK chunk IN PARALLEL (it ships separately;
  // riding the token round-trip hides the chunk latency).
  marks = { start: performance.now(), preloaded: isDailyPreloaded() };
  timingPosted = false;
  const dailyPromise = loadDaily();
  // Surface a chunk failure through the same error path as a token
  // failure instead of an unhandled rejection if the token throws first.
  dailyPromise.then(() => mark('chunk')).catch(() => {});
  const tokenResp = await fetchVoiceToken(opts);
  mark('token');
  activeOpts = opts;
  activeSessionId = tokenResp.sessionId;
  activeRoom =
    tokenResp.roomName && typeof tokenResp.roomExpiresAt === 'number' && tokenResp.roomSig
      ? {
          roomName: tokenResp.roomName,
          roomUrl: tokenResp.roomUrl,
          expiresAt: tokenResp.roomExpiresAt,
          sig: tokenResp.roomSig,
        }
      : null;
  respawnAttempts = 0;

  // 2. Move to CONNECTING. The greeter video doubles as a loading
  // curtain while Daily warms up — the visitor sees motion instead
  // of a frozen UI.
  voiceTransition.toConnecting(tokenResp.roomUrl, tokenResp.sessionId);

  // 3. Build the Daily call object. We intentionally use
  // `createCallObject` (vs `createFrame`) — the latter mounts an
  // iframe with Daily's prebuilt UI; we want headless audio that
  // composes into our own widget chrome.
  let call: DailyCall;
  try {
    const Daily = await dailyPromise;
    // The warmed object (preloadVoiceRuntime: created + load()ed at idle)
    // when there is one — the call machine is already evaluated.
    call = takePreloadedCall() ?? Daily.createCallObject(CALL_OBJECT_OPTIONS);
  } catch (err) {
    voiceTransition.toError(`failed to create Daily call: ${describe(err)}`);
    throw err;
  }
  activeCall = call;
  earsReadySent = false;

  // 4. Wire events BEFORE joining so we don't miss the initial flurry.
  wireCallEvents(call);

  // 5. Join. Daily promises resolve after the join transaction
  // completes, but the bot may still be coming up — we don't move
  // to LISTENING until the bot participant appears.
  try {
    await call.join({ url: tokenResp.roomUrl, token: tokenResp.token });
  } catch (err) {
    voiceTransition.toError(`failed to join room: ${describe(err)}`);
    await safeLeave(call);
    activeCall = null;
    throw err;
  }
  mark('join');
  // Audio-only calls never paint a face: post what we have after a while.
  setTimeout(postConnectTiming, 12_000);

  // Rebind the live avatar for warm rejoins — the bot's continuous
  // video track never re-fires `track-started` on this call object.
  sweepBotVideo(call);

  return tokenResp;
}

export async function stopVoiceSession(): Promise<void> {
  const call = activeCall;
  activeCall = null;
  if (botEndFallbackTimer) {
    clearTimeout(botEndFallbackTimer);
    botEndFallbackTimer = null;
  }
  onBotEndedCb = null;
  botEndedIntentionally = false;
  activeOpts = null;
  activeRoom = null;
  activeSessionId = null;
  respawnAttempts = 0;
  if (respawnJoinTimer) {
    clearTimeout(respawnJoinTimer);
    respawnJoinTimer = null;
  }
  try {
    // Tell the bot this is a DELIBERATE hang-up (visitor pressed End / idle
    // end), not a page-nav drop, so it skips the reconnect grace window and
    // frees its Simli/Daily resources immediately instead of lingering ~20s.
    // Best-effort: if the leave races the message the bot just falls back to
    // its idle backstop. We do NOT send this on page-unload (the page is
    // gone before stopVoiceSession runs) — that drop SHOULD get the grace.
    if (call) {
      try {
        call.sendAppMessage({ type: 'showpilot:visitor-ended' }, '*');
      } catch {
        /* data channel may already be down — bot falls back to idle */
      }
    }
    runTeardowns();
    if (call) await safeLeave(call);
  } finally {
    voiceTransition.toIdle();
  }
}

/**
 * Hot-mute toggle. Daily exposes `setLocalAudio(false)` for soft mute
 * (track stays subscribed, audio is gated). We surface the flag in
 * voiceState so the UI can render the muted mic icon.
 */
export function setVoiceMicMuted(muted: boolean): void {
  if (!activeCall) return;
  activeCall.setLocalAudio(!muted);
  voiceState.value = { ...voiceState.value, micMuted: muted };
}

/** A selectable microphone input. */
export type VoiceAudioInput = { deviceId: string; label: string };

/*
 * v2 (audio-source picker): the two helpers below are the dormant seed for an
 * in-call mic chooser. Not wired into the UI yet — the live-call pill is
 * mute · End and the call uses the OS-default mic. Kept because they're ~15
 * lines and v2-ready; the chevron UI lived in VideoGreeter and was stripped.
 */

/**
 * Enumerate the visitor's microphone inputs for the in-call source picker,
 * marking the one currently feeding the call. Daily returns labels only
 * after mic permission is granted (which a live call implies). Returns []
 * when there's no active call.
 */
export async function listVoiceAudioInputs(): Promise<{
  inputs: VoiceAudioInput[];
  activeDeviceId: string | null;
}> {
  const toInputs = (devices: MediaDeviceInfo[]): VoiceAudioInput[] =>
    devices
      .filter((d) => d.kind === 'audioinput' && d.deviceId)
      .map((d, i) => ({
        deviceId: d.deviceId,
        label: d.label || `Microphone ${i + 1}`,
      }));
  // No active call (e.g. dev preview): best-effort list from the platform.
  // Switching is still gated on an active call, so this is list-only.
  if (!activeCall) {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return { inputs: toInputs(devices), activeDeviceId: null };
    } catch {
      return { inputs: [], activeDeviceId: null };
    }
  }
  let activeDeviceId: string | null = null;
  try {
    const current = await activeCall.getInputDevices();
    const mic = current?.mic;
    // Daily types mic as MediaDeviceInfo | false | {}; guard for a real entry.
    if (mic && typeof mic === 'object' && 'deviceId' in mic) {
      activeDeviceId = (mic as MediaDeviceInfo).deviceId || null;
    }
  } catch {
    /* fall through — we can still list devices */
  }
  const { devices } = await activeCall.enumerateDevices();
  return { inputs: toInputs(devices), activeDeviceId };
}

/** Switch the call's microphone input to the chosen device. */
export async function setVoiceAudioInput(deviceId: string): Promise<void> {
  if (!activeCall) return;
  await activeCall.setInputDevicesAsync({ audioDeviceId: deviceId });
}

/**
 * Send the current page URL to the bot when the visitor client-side
 * navigates within an SPA host. The bot subprocess stays alive across
 * SPA navs (the page never unloads), so without this its currentPageUrl
 * is frozen at the spawn-time value and it will keep referencing the
 * wrong page. Bot's on_app_message handler injects a system note into
 * the live LLM context.
 */
export function notifyVoiceOfPageChange(url: string): void {
  // Stamp the time BEFORE the bot is told — handleChatEvent uses this
  // to drop stale tool side-effects (content_view / navigate / etc)
  // that the model dispatched based on pre-nav context. The window is
  // ~1.5s — typical tool-dispatch latency from "LLM decides" to "widget
  // receives SSE side-effect." Outside that window legitimate tool
  // calls take priority.
  lastWidgetNavAt = Date.now();
  if (!activeCall) return;
  try {
    activeCall.sendAppMessage({ type: 'showpilot:page-changed', url }, '*');
  } catch (err) {
    console.warn('[voice/daily-session] page-change app-message failed', err);
  }
}

let lastWidgetNavAt = 0;
const STALE_TOOL_WINDOW_MS = 1500;
/**
 * True when the visitor navigated within the last ~1.5s. handleChatEvent
 * calls this before executing `open-content` / `navigate-url` to gate
 * out side-effects from tool calls the agent dispatched based on the
 * pre-nav user statement. Highlight already has cross-page handling so
 * doesn't go through this gate. Bug context: voice-bot Alex 2026-05-18,
 * "Let's watch YouTube AI → visitor navigates → content_view fires on
 * new page."
 */
export function isInStaleToolWindow(): boolean {
  return lastWidgetNavAt > 0 && Date.now() - lastWidgetNavAt < STALE_TOOL_WINDOW_MS;
}

/**
 * Notify the bot that the visitor just took an action the agent
 * curated — clicked an agent link, opened content, etc. Bot's
 * on_app_message handler interrupts in-flight TTS and injects a
 * system note so the next utterance acknowledges the action instead
 * of continuing past it.
 *
 * Returns true if the message was sent (voice is active); false
 * otherwise so the caller knows to fall back to the text-mode
 * synthetic-message path.
 */
/**
 * First-turn hold (2026-10-26). The bot used to speak its first line the
 * instant the visitor joined — into the loading card, before the face had
 * painted — so the visitor heard the pickup line from mid-sentence. The
 * widget is the only party that knows when the face is on screen
 * (`liveReady`, first painted frame) and when the bot's audio sink is
 * attached; it says so, and the bot holds its first run for it. One
 * `ears-ready` per call; `face-ready` whenever a live face (re)paints.
 */
let earsReadySent = false;

// Connect timing (connect-timing.ts): one event per call, posted when the
// face paints or, failing that, 12 s after the join.
let marks: ConnectMarks | null = null;
let timingPosted = false;
function mark(step: 'token' | 'chunk' | 'join' | 'bot' | 'face'): void {
  if (marks && marks[step] === undefined) marks[step] = performance.now();
}
function postConnectTiming(): void {
  if (timingPosted || !marks || !activeOpts || !activeSessionId) return;
  timingPosted = true;
  postSessionEvent(activeOpts.chatApiBaseUrl, activeSessionId, {
    type: 'voice-connect-timing',
    payload: connectTimingPayload(marks),
  });
}

/** The live face painted — the last mark of the connect timeline. */
export function markVoiceFaceReady(): void {
  mark('face');
  postConnectTiming();
}

export function notifyVoiceVisitorReady(kind: 'face-ready' | 'ears-ready'): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage({ type: `showpilot:${kind}` }, '*');
    return true;
  } catch (err) {
    console.warn('[voice/daily-session] visitor-ready app-message failed', err);
    return false;
  }
}

export function notifyVoiceOfVisitorAction(action: {
  kind: 'open_content' | 'open_url' | 'book_meeting' | 'form_submitted' | 'meeting_booked';
  label: string;
  url?: string;
  contentTitle?: string;
}): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage({ type: 'showpilot:visitor-action', ...action }, '*');
    return true;
  } catch (err) {
    console.warn('[voice/daily-session] visitor-action app-message failed', err);
    return false;
  }
}

/**
 * ONE VOICE for typed text: the visitor TYPED a message during a live
 * call. The bot answers it aloud (and persists it as a typed transcript);
 * the widget must NOT also run a /chat turn — that was two brains on one
 * message (text reply + spoken reply, live 2026-09-08). Returns false
 * when no call is live so the caller runs the normal text turn.
 */
export function notifyVoiceOfVisitorText(text: string): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage({ type: 'showpilot:visitor-text', text }, '*');
    return true;
  } catch (err) {
    console.warn('[voice/daily-session] visitor-text app-message failed', err);
    return false;
  }
}

/**
 * STAGE-TRUTH LOOP: the VIEWER moved the tour on the stage themselves
 * (clicked/keyed to another screen) during a live call. The bot gets a
 * silent context note — no proactive turn (a chatty reaction to every
 * click would fight the viewer for the wheel); its next reply and next
 * tourControl simply start from the screen they're really on. Returns
 * false when no call is live (text mode covers itself via the
 * presenter-screen session event).
 */
export function notifyVoiceOfScreenChange(info: { screen: number; total?: number }): boolean {
  if (!activeCall) return false;
  try {
    // undefined total drops out in JSON serialization — no spread dance.
    activeCall.sendAppMessage({ type: 'showpilot:screen-changed', ...info }, '*');
    return true;
  } catch {
    return false;
  }
}

/**
 * TOUR-END OUTCOME (slice 4): the tour on the stage reached its end (or
 * its completion step) during a live call — the landing moment. The
 * bot's handler injects the note + a PROACTIVE context frame (unlike
 * screen-changed's silent note): dead air at the point of highest
 * intent is the worst possible silence. False when no call is live —
 * the caller falls back to the hidden text nudge.
 */
export function notifyVoiceOfTourEnded(): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage({ type: 'showpilot:tour-ended' }, '*');
    return true;
  } catch {
    return false;
  }
}

/**
 * STAGE TRUTH — the content viewer CLOSED (viewer's X, backdrop, agent
 * close/navigate): nothing is on the visitor's screen but the page.
 * Until 2026-10-27 only chat-api's stage memory heard this; the bot
 * kept "continuing the demo" and talking about a guide the visitor had
 * closed (showpilot.com, David: "she thought I was still looking at the
 * guide"). Returns false when no call is live.
 */
export function notifyVoiceOfContentClosed(contentTitle: string | null): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage(
      { type: 'showpilot:content-closed', ...(contentTitle ? { contentTitle } : {}) },
      '*',
    );
    return true;
  } catch (err) {
    console.warn('[voice/daily-session] content-closed app-message failed', err);
    return false;
  }
}

/**
 * ONE-VOICE LAW: the narrated video on stage just ENDED — the bot's cue
 * to retake the thread (one line on what it showed, then the next step)
 * instead of leaving dead air. Bot's on_app_message handler injects the
 * note + triggers a proactive turn, same mechanism as page-changed.
 * Returns false when no call is live so the caller falls back to the
 * text-mode hidden nudge.
 */
export function notifyVoiceOfMediaEnded(contentTitle: string | null): boolean {
  if (!activeCall) return false;
  try {
    activeCall.sendAppMessage(
      { type: 'showpilot:media-ended', ...(contentTitle ? { contentTitle } : {}) },
      '*',
    );
    return true;
  } catch (err) {
    console.warn('[voice/daily-session] media-ended app-message failed', err);
    return false;
  }
}

/**
 * Push the current page snapshot (URL + visible elements) to chat-api
 * so the bot can fetch it on each LLM turn and answer
 * page-aware questions / highlight on-screen text. Fire-and-forget;
 * a dropped push just means the bot's next turn uses slightly stale
 * context (acceptable — the visitor's next nav will push again).
 *
 * Called from the voice bootstrap on session-start AND from the SPA
 * page-change watcher. Mirrors `notifyVoiceOfPageChange` but ALSO
 * delivers the page elements (which Daily app-message can't easily
 * carry — too large for the data channel).
 */
export async function pushVoicePageContext(opts: {
  chatApiBaseUrl: string;
  sessionId: string;
  publicId: string;
  url: string;
  pageElements: Array<{ id: string; tagName: string; text: string; href?: string }>;
  /** E8 — the greeting the visitor already read on the page, so the bot picks up instead of re-greeting. */
  surfaceGreeting?: string | null;
}): Promise<void> {
  // The visit so far rides every push — the bot renders `# This visit`
  // from it at pickup and after each page change (slice 2, 2026-10-28).
  const visit = visitSnapshot();
  try {
    await fetch(`${opts.chatApiBaseUrl.replace(/\/+$/, '')}/v1/voice/page-context`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        sessionId: opts.sessionId,
        publicId: opts.publicId,
        url: opts.url,
        pageElements: opts.pageElements,
        ...(opts.surfaceGreeting ? { surfaceGreeting: opts.surfaceGreeting.slice(0, 600) } : {}),
        ...(visit ? { visit } : {}),
      }),
      keepalive: true,
    });
  } catch (err) {
    console.warn('[voice/daily-session] page-context push failed', err);
  }
}

/** Convenience for the UI to know if voice is set up enough to mute. */
export function hasActiveVoiceCall(): boolean {
  return activeCall !== null;
}

// ─── Internals ─────────────────────────────────────────────────────────

async function fetchVoiceToken(opts: StartVoiceSessionOptions): Promise<VoiceTokenResponse> {
  // Read fingerprint lazily — voice can be initiated before any text
  // turn has stamped the cookie. getOrCreateVisitorFingerprint will
  // stamp on first call so the visitor row is joined to this voice
  // session and the bot's prompt picks up the visitor block.
  const { getOrCreateVisitorFingerprint } = await import('../visitor-id');
  const fingerprint = getOrCreateVisitorFingerprint();

  const url = `${opts.chatApiBaseUrl.replace(/\/+$/, '')}/v1/agents/${opts.publicId}/voice/token`;
  // The visit rides the token request: the server seeds it for the bot
  // BEFORE the bot is claimed (the page-context push below races the join).
  const visit = visitSnapshot();
  const resp = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      pageUrl: opts.pageUrl ?? null,
      // Presenter — flips the bot into the PRESENTING stance + stage tools.
      ...(opts.projectPublicId ? { projectPublicId: opts.projectPublicId } : {}),
      ...(opts.projectPublicId && opts.tourPublicId ? { tourPublicId: opts.tourPublicId } : {}),
      ...(opts.presentDoor ? { presentDoor: true } : {}),
      // Pass through the visitor's existing chat sessionId so voice +
      // text interleave under one chat_sessions row.
      ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
      // Warm-reconnect hint — rejoin the same room (bot held alive in its
      // grace window) instead of a cold summon. Omitted on fresh starts.
      ...(opts.rejoinRoom ? { rejoinRoom: opts.rejoinRoom } : {}),
      // The bot vanished mid-call — spawn a fresh one into that room.
      ...(opts.respawnBot ? { respawnBot: true } : {}),
      // R1 visitor identity — server upserts visitors row + joins this
      // session to it before the bot spawns, so the bot's agent-config
      // fetch sees the visitor block in the system prompt.
      fingerprint,
      ...(visit ? { visit } : {}),
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`/voice/token failed: ${resp.status} ${text}`);
  }
  return resp.json() as Promise<VoiceTokenResponse>;
}

/**
 * Bind the bot's live avatar video from a participant snapshot. Daily
 * sometimes delivers the Simli track via `participant-updated` (custom /
 * late-published video) rather than a clean `track-started`, so we bind
 * from the participant track state as a backstop to the event handler.
 * Module-scope so the post-join sweep (warm rejoin) can reuse it.
 */
function bindBotVideo(p: DailyEventObjectParticipant['participant'] | undefined): void {
  // Only the bot (owner) carries the live avatar video; ignore the local
  // visitor and any non-owner participant (e.g. a relay rep) so their
  // track state can't bind or clear the avatar stream.
  if (!p || p.local || !p.owner) return;
  const v = p.tracks?.video as
    | { state?: string; track?: MediaStreamTrack; persistentTrack?: MediaStreamTrack }
    | undefined;
  if (!v) return;
  const track = v.persistentTrack ?? v.track ?? null;
  const playable = v.state === 'playable' || v.state === 'sendable' || v.state === 'loading';
  if (playable && track) {
    const cur = botVideoStream.value?.getVideoTracks?.()[0] ?? null;
    if (cur?.id !== track.id) {
      botVideoStream.value = new MediaStream([track]);
    }
  }
  // Do NOT null the stream here on a transient non-playable snapshot.
  // `participant-updated` fires with momentary non-playable video states
  // during renegotiation — right as Simli's track comes up — and clearing
  // on those made the live <video> unmount → summon overlay reappear →
  // remount (the engage flicker/collapse/reload). The authoritative
  // teardown is onTrackStopped (real track end) + onParticipantLeft (bot
  // leaves); this backstop only ever UPGRADES to the live track.
}

/**
 * Warm-rejoin video backstop. On a page-nav rejoin the bot is ALREADY
 * in the room with its Simli video LIVE — that track's `track-started`
 * fired in the PREVIOUS page's call object and never re-fires for this
 * one, so the rail reloaded with audio (Pipecat republishes a fresh
 * audio track per TTS segment) but a dead avatar (David prod catch
 * 2026-07-15). Sweep the participant snapshot after join; retry twice
 * because the subscription may still be renegotiating at join-resolve.
 * Idempotent — bindBotVideo only ever upgrades to a live track.
 */
function sweepBotVideo(call: DailyCall): void {
  const sweep = () => {
    if (activeCall !== call) return; // session ended/replaced — stale timer
    try {
      for (const p of Object.values(call.participants())) {
        bindBotVideo(p as DailyEventObjectParticipant['participant']);
      }
    } catch {
      /* participants() throws after leave — nothing to bind */
    }
  };
  sweep();
  setTimeout(sweep, 1000);
  setTimeout(sweep, 3000);
}

function wireCallEvents(call: DailyCall): void {
  /** Bot's participant id; set when the bot joins the room. */
  let botParticipantId: string | null = null;

  /**
   * When the bot publishes an audio track we move to SPEAKING. When
   * the track stops (Pipecat ends a TTS segment) we move back to
   * LISTENING. Daily fires `track-started` / `track-stopped` per
   * track — we filter to the bot's audio.
   */

  const onTrackStarted = (ev: DailyEventObjectTrack) => {
    if (ev.participant?.local) return;
    if (!botParticipantId) botParticipantId = ev.participant?.session_id ?? null;

    // Live video avatar (Simli). When the agent runs an avatar the bot
    // publishes a video track; surface it as a MediaStream the video
    // greeter renders as the live talking head. We don't gate audio on
    // it — audio + video are separate Daily tracks.
    if (ev.track.kind === 'video') {
      botVideoStream.value = new MediaStream([ev.track]);
      return;
    }

    if (ev.track.kind !== 'audio') return;

    // Headless-mode audio sink. Without this the visitor hears
    // nothing — Daily decodes the track but won't play it on its
    // own. autoplay + muted=false: visitor's "Talk Now" click
    // counts as the user gesture that unlocks audio playback.
    const trackId = ev.track.id;
    if (!audioSinks.has(trackId)) {
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.muted = botAudioMuted;
      audio.style.display = 'none';
      audio.srcObject = new MediaStream([ev.track]);
      document.body.appendChild(audio);
      audioSinks.set(trackId, audio);
      audio.play().catch((err) => {
        console.warn('[voice/daily-session] audio playback blocked', err);
      });
    }
    // The visitor can hear the bot from here — release its first turn on
    // audio-only calls (avatar calls wait for the face instead).
    if (!earsReadySent) {
      earsReadySent = true;
      notifyVoiceVisitorReady('ears-ready');
    }

    // Playout ears — analyser tap beside the sink (rails 1.1).
    attachPlayoutSource(ev.track);

    voiceTransition.toSpeaking();
  };

  const onTrackStopped = (ev: DailyEventObjectTrack) => {
    if (ev.participant?.local) return;

    // Live video avatar track ended — drop the stream so the greeter
    // falls back to its pre-recorded video / still.
    if (ev.track.kind === 'video') {
      botVideoStream.value = null;
      return;
    }

    if (ev.track.kind !== 'audio') return;

    const trackId = ev.track.id;
    const audio = audioSinks.get(trackId);
    if (audio) {
      audio.srcObject = null;
      audio.remove();
      audioSinks.delete(trackId);
    }
    detachPlayoutSource(trackId);

    if (voiceState.value.name === 'speaking') {
      voiceTransition.toListening();
    }
    botTalking.value = false; // no audio track, no talking
  };

  /**
   * Daily app-messages used for in-call signals that don't fit the
   * SSE data plane shape — currently just barge-in. Transcripts ride
   * the chat-api SSE channel (audio + data-plane split — audio over
   * Daily WebRTC, transcripts + tool calls over SSE); see bootstrap.ts
   * for the wiring.
   */
  const onAppMessage = (ev: { fromId?: string; data?: unknown }) => {
    if (typeof ev.data !== 'object' || !ev.data) return;
    const d = ev.data as { type?: string };
    if (d.type === 'showpilot:bot-speaking') {
      // Per-utterance talk signal from the bot's own output audio
      // (BotStarted/StoppedSpeakingFrame) — drives the one-voice law's
      // media floor. See botTalking in voice-state.ts for why the
      // track-level 'speaking' state can't do this job.
      const on = (d as { on?: boolean }).on === true;
      if (playoutMonitor) {
        // With ears on the line the monitor owns BOTH edges of
        // botTalking: on at generation start, off when the voice has
        // actually finished playing (an avatar vendor in the chain runs
        // seconds behind the generation edge — dropping the signal here
        // brought paused media back up under her voice).
        playoutMonitor.speaking(on, Date.now());
      } else {
        botTalking.value = on;
      }
      return;
    }
    if (d.type === 'pipecat:interrupted') {
      botTalking.value = false; // TTS is being cancelled
      playoutMonitor?.interrupted(); // barge — the turn never played out, no ack
      voiceTransition.toInterrupted();
      return;
    }
    if (d.type === 'showpilot:session-ended') {
      // Bot is ending the call on its own (idle timeout). Mark it so the
      // bot's imminent participant-left reads as a CLEAN end (no DEGRADED)
      // and so the caller clears cross-page continuity. We DON'T tear down
      // here — the bot speaks a short goodbye first, then leaves; we close
      // on its participant-left so the farewell actually plays. A fallback
      // timer guarantees teardown if that event is ever missed.
      botEndedIntentionally = true;
      if (botEndFallbackTimer) clearTimeout(botEndFallbackTimer);
      botEndFallbackTimer = setTimeout(() => {
        botEndFallbackTimer = null;
        if (botEndedIntentionally && activeCall) onBotEndedCb?.();
      }, 6000);
      return;
    }
  };

  const onParticipantJoined = (ev: DailyEventObjectParticipant) => {
    if (ev.participant.local) return;
    if (ev.participant.owner) {
      botParticipantId = ev.participant.session_id;
      mark('bot');
      // A respawned bot made it into the room — the call continues.
      if (respawnJoinTimer) {
        clearTimeout(respawnJoinTimer);
        respawnJoinTimer = null;
        console.log('[voice/daily-session] respawned bot joined');
      }
      // First real participant other than us — flip to LISTENING.
      // We still wait for the bot's first track to fire SPEAKING.
      voiceTransition.toListening();
    }
    bindBotVideo(ev.participant);
  };

  const onParticipantUpdated = (ev: DailyEventObjectParticipant) => {
    bindBotVideo(ev.participant);
  };

  /**
   * The bot is gone from the call — reached from Daily's `participant-left`
   * for the bot's id, or early from chat-api's `voice-bot-dropped` signal
   * (botDroppedHook) when the spawn server saw the process die.
   */
  const botGone = () => {
    if (botEndedIntentionally) {
      // Clean, bot-initiated end (idle timeout) — the goodbye has played,
      // the bot has left. Tear down via the registered callback so the
      // caller clears continuity (no auto-reopen). NOT a crash → no
      // DEGRADED.
      if (botEndFallbackTimer) {
        clearTimeout(botEndFallbackTimer);
        botEndFallbackTimer = null;
      }
      onBotEndedCb?.();
      return;
    }
    // Bot left unexpectedly — its process died (a deploy past the drain
    // cap, a crash). The visitor is still in the room and the transcript
    // is stored: show DEGRADED and ask the server for a fresh bot into
    // THIS room (bot-respawn.ts). Bounded; past the cap the call ends as
    // an error through the same teardown an intentional end uses.
    voiceTransition.toDegraded();
    const nowSec = Math.floor(Date.now() / 1000);
    if (
      !activeOpts ||
      !shouldRespawnBot({ intentional: false, attempts: respawnAttempts, room: activeRoom, nowSec })
    ) {
      voiceTransition.toError('The agent dropped from the call and could not reconnect.');
      onBotEndedCb?.();
      return;
    }
    respawnAttempts += 1;
    botParticipantId = null; // the next owner join is the new bot
    console.warn(`[voice/daily-session] bot left unexpectedly — respawn ${respawnAttempts}`);
    const opts = activeOpts;
    const room = activeRoom;
    void fetchVoiceToken({
      ...opts,
      sessionId: activeSessionId ?? opts.sessionId,
      rejoinRoom: room,
      respawnBot: true,
      presentDoor: false,
    })
      .then(() => {
        if (activeCall !== call) return; // session ended meanwhile
        if (respawnJoinTimer) clearTimeout(respawnJoinTimer);
        respawnJoinTimer = setTimeout(() => {
          respawnJoinTimer = null;
          if (activeCall !== call || botParticipantId) return;
          voiceTransition.toError('The agent dropped from the call and did not come back.');
          onBotEndedCb?.();
        }, RESPAWN_JOIN_TIMEOUT_MS);
      })
      .catch((err: unknown) => {
        if (activeCall !== call) return;
        console.warn('[voice/daily-session] respawn request failed', err);
        voiceTransition.toError('The agent dropped from the call and could not reconnect.');
        onBotEndedCb?.();
      });
  };

  const onParticipantLeft = (ev: DailyEventObjectParticipantLeft) => {
    if (ev.participant.session_id !== botParticipantId) return;
    botGone();
  };

  botDroppedHook = () => {
    // No bot in the room (not joined yet, or already being replaced):
    // nothing to do — the join path and its timeout handle those.
    if (!botParticipantId || activeCall !== call) return false;
    console.warn('[voice/daily-session] server reports the bot process died — not waiting for Daily');
    botGone();
    return true;
  };

  const onNetworkQuality = (ev: DailyEventObjectNetworkQualityEvent) => {
    // Daily's quality bucket is 'good' | 'low' | 'very-low'.
    if (ev.threshold === 'very-low') {
      voiceTransition.toDegraded();
    } else if (voiceState.value.name === 'degraded' && ev.threshold === 'good') {
      voiceTransition.toListening();
    }
  };

  const onFatalError = (ev: DailyEventObjectFatalError) => {
    voiceTransition.toError(ev.errorMsg || 'voice session failed');
  };

  const onLeft = () => {
    if (voiceState.value.name !== 'idle' && voiceState.value.name !== 'error') {
      voiceTransition.toIdle();
    }
  };

  // Available-devices event surfaces when the visitor's mic is
  // unplugged mid-call. Phase 1 just logs; UI hookup is deferred.
  const onDevicesUpdated = (_ev: DailyEventObjectAvailableDevicesUpdated) => {
    // no-op for Phase 1
  };

  call.on('track-started', onTrackStarted);
  call.on('track-stopped', onTrackStopped);
  call.on('app-message', onAppMessage);
  call.on('participant-joined', onParticipantJoined);
  call.on('participant-updated', onParticipantUpdated);
  call.on('participant-left', onParticipantLeft);
  call.on('network-quality-change', onNetworkQuality);
  call.on('error', onFatalError);
  call.on('left-meeting', onLeft);
  call.on('available-devices-updated', onDevicesUpdated);

  teardownFns.push(() => {
    botDroppedHook = null;
    call.off('track-started', onTrackStarted);
    call.off('track-stopped', onTrackStopped);
    call.off('app-message', onAppMessage);
    call.off('participant-joined', onParticipantJoined);
    call.off('participant-left', onParticipantLeft);
    call.off('network-quality-change', onNetworkQuality);
    call.off('error', onFatalError);
    call.off('left-meeting', onLeft);
    call.off('available-devices-updated', onDevicesUpdated);
  });
}

function runTeardowns(): void {
  const fns = teardownFns;
  teardownFns = [];
  for (const fn of fns) {
    try {
      fn();
    } catch (err) {
      console.warn('[voice/daily-session] teardown threw', err);
    }
  }
  // Drop the live avatar stream so the greeter reverts to its
  // pre-recorded video / still after the call ends.
  botVideoStream.value = null;
  // Final-pass audio-sink cleanup — track-stopped usually clears each
  // one, but if a call ends mid-track (visitor closed the tab while
  // the bot was speaking) the sink would survive.
  for (const audio of audioSinks.values()) {
    audio.srcObject = null;
    audio.remove();
  }
  audioSinks.clear();
  stopPlayoutEars();
}

async function safeLeave(call: DailyCall): Promise<void> {
  try {
    await call.leave();
  } catch (err) {
    console.warn('[voice/daily-session] leave threw', err);
  }
  try {
    call.destroy();
  } catch (err) {
    console.warn('[voice/daily-session] destroy threw', err);
  }
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
